import { FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { usePlans } from "@/hooks/usePlans";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface PlanCustomizationStepProps {
  form: UseFormReturn<any>;
}

export function PlanCustomizationStep({ form }: PlanCustomizationStepProps) {
  const { plans } = usePlans();
  const selectedPlanId = form.watch("planId");
  const selectedPlan = plans?.find(p => p.id === selectedPlanId);
  const customAdvancedMetrics = form.watch("customAdvancedMetrics") || [];
  const customIntegrations = form.watch("customIntegrations") || [];

  const hasCustomization = 
    selectedPlan?.advanced_metrics_mode === 'customizable' || 
    selectedPlan?.integrations_mode === 'customizable';

  if (!hasCustomization) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Configuração do Plano</h3>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            O plano <strong>{selectedPlan?.name}</strong> possui configurações pré-definidas. 
            Você pode prosseguir para revisar os dados antes de criar a tenant.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Configuração do Plano</h3>
      <p className="text-sm text-muted-foreground">
        Personalize as métricas e integrações para o plano <strong>{selectedPlan?.name}</strong>
      </p>

      {selectedPlan?.advanced_metrics_mode === 'customizable' && (
        <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
          <h4 className="font-semibold text-sm">Personalizar Métricas Avançadas</h4>
          <p className="text-xs text-muted-foreground">
            Selecione até {selectedPlan.max_advanced_metrics} métricas avançadas
          </p>
          <FormField
            control={form.control}
            name="customAdvancedMetrics"
            render={() => (
              <FormItem>
                <div className="grid grid-cols-2 gap-2">
                  {selectedPlan.advanced_metrics.map((metric) => (
                    <FormField
                      key={metric}
                      control={form.control}
                      name="customAdvancedMetrics"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(metric)}
                              disabled={
                                !field.value?.includes(metric) && 
                                field.value?.length >= selectedPlan.max_advanced_metrics
                              }
                              onCheckedChange={(checked) => {
                                const current = field.value || [];
                                if (checked) {
                                  if (current.length < selectedPlan.max_advanced_metrics) {
                                    field.onChange([...current, metric]);
                                  }
                                } else {
                                  field.onChange(current.filter((v: string) => v !== metric));
                                }
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            {metric}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {customAdvancedMetrics.length} de {selectedPlan.max_advanced_metrics} selecionadas
                </p>
              </FormItem>
            )}
          />
        </div>
      )}

      {selectedPlan?.integrations_mode === 'customizable' && (
        <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
          <h4 className="font-semibold text-sm">Personalizar Integrações</h4>
          <p className="text-xs text-muted-foreground">
            Selecione até {selectedPlan.max_integrations} integrações
          </p>
          <FormField
            control={form.control}
            name="customIntegrations"
            render={() => (
              <FormItem>
                <div className="grid grid-cols-2 gap-2">
                  {selectedPlan.allowed_integrations.map((integration) => (
                    <FormField
                      key={integration}
                      control={form.control}
                      name="customIntegrations"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(integration)}
                              disabled={
                                !field.value?.includes(integration) && 
                                field.value?.length >= selectedPlan.max_integrations
                              }
                              onCheckedChange={(checked) => {
                                const current = field.value || [];
                                if (checked) {
                                  if (current.length < selectedPlan.max_integrations) {
                                    field.onChange([...current, integration]);
                                  }
                                } else {
                                  field.onChange(current.filter((v: string) => v !== integration));
                                }
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            {integration}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {customIntegrations.length} de {selectedPlan.max_integrations} selecionadas
                </p>
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
